import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * Formal, table-style month row for the By-Month tab. Slate accents, no
 * rounded cards — reads like a financial statement.
 */
export function IncomeMonthRow({ month, year, isCurrentMonth, defaultOpen, highlightTotal }) {
  const [expanded, setExpanded] = useState(defaultOpen || false);
  const [expandedProps, setExpandedProps] = useState({});
  const [expandedUnits, setExpandedUnits] = useState({});

  const toggleProp = (key) => setExpandedProps((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleUnit = (key) => setExpandedUnits((prev) => ({ ...prev, [key]: !prev[key] }));

  const pct = highlightTotal ? Math.round(((month.total || 0) / highlightTotal) * 100) : 0;

  return (
    <div className={`border-b border-slate-200 last:border-b-0 ${isCurrentMonth ? 'bg-emerald-50/30' : ''}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 grid grid-cols-[24px_1fr_auto_80px] items-center gap-3 text-left hover:bg-slate-50 transition-colors"
        data-testid="income-month-row-toggle"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">{MONTH_NAMES[month.month]} {year}</span>
          {isCurrentMonth && <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">Current</span>}
          {month.total === 0 && !expanded && <span className="text-[11px] text-slate-400 italic">no activity</span>}
        </div>
        <span className="text-sm font-semibold tabular-nums text-slate-900">${fmt(month.total)}</span>
        <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
          <div className="h-full bg-slate-700" style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 bg-slate-50/40">
          {month.properties.length === 0 ? (
            <div className="pl-8 py-2 text-xs text-slate-400 italic">No income this month</div>
          ) : (
            <div className="divide-y divide-slate-200 border border-slate-200 rounded bg-white">
              {month.properties.map((prop) => {
                const propKey = `${month.month}-${prop.property_id}`;
                const propOpen = expandedProps[propKey];
                return (
                  <div key={propKey}>
                    <button
                      type="button"
                      onClick={() => toggleProp(propKey)}
                      className="w-full px-4 py-2 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {propOpen ? <ChevronDown className="h-3 w-3 text-slate-500" /> : <ChevronRight className="h-3 w-3 text-slate-500" />}
                        <span className="text-[13px] font-medium text-slate-800">{prop.property_name}</span>
                        <span className="text-[10px] text-slate-400">{prop.units.length} unit{prop.units.length === 1 ? '' : 's'}</span>
                      </div>
                      <span className="text-[13px] font-semibold tabular-nums text-slate-900">${fmt(prop.total)}</span>
                    </button>
                    {propOpen && (
                      <div className="divide-y divide-slate-100 bg-slate-50/40">
                        {prop.units.map((unit) => {
                          const unitKey = `${propKey}-${unit.unit_id}`;
                          const unitOpen = expandedUnits[unitKey];
                          return (
                            <div key={unitKey}>
                              <button
                                type="button"
                                onClick={() => toggleUnit(unitKey)}
                                className="w-full pl-10 pr-4 py-1.5 flex items-center justify-between gap-3 hover:bg-white transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  {unitOpen ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
                                  <span className="text-[12px] text-slate-700">Unit {unit.unit_number}</span>
                                </div>
                                <span className="text-[12px] tabular-nums text-slate-800">${fmt(unit.total)}</span>
                              </button>
                              {unitOpen && (
                                <div className="pl-16 pr-4 pb-2 bg-white">
                                  {unit.tenants.map((t, i) => (
                                    <div key={i}>
                                      <div className="flex items-center justify-between py-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[11px] text-slate-600">{t.tenant_name}</span>
                                          {t.is_airbnb && <span className="text-[9px] uppercase tracking-wider font-medium px-1 py-px rounded bg-sky-100 text-sky-800 border border-sky-200">Airbnb</span>}
                                        </div>
                                        <span className="text-[11px] tabular-nums text-slate-700">${fmt(t.income)}</span>
                                      </div>
                                      {t.misc_charges && t.misc_charges.map((mc, mi) => (
                                        <div key={mi} className="flex items-center justify-between py-0.5 pl-4">
                                          <span className="text-[10px] text-slate-400 italic">Misc: {mc.description || 'Misc charge'}</span>
                                          <span className="text-[10px] tabular-nums text-slate-500">${fmt(mc.amount)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
